import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Channel, ChannelType, WsEvent } from '../../shared/models/chat.models';
import { ChatService } from '../../shared/services/chat.service';

type ContactUser = {
  user_id: number;
  user_name: string;
  email: string;
  role_name: string;
};

type ChannelsSubTab = 'group' | 'entity_linked';

@Component({
  selector: 'app-channel-list',
  templateUrl: './channel-list.component.html',
  styleUrls: ['./channel-list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, ButtonModule],
})
export class ChannelListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() activeChannelId: number | null = null;
  @Input() viewTab: 'all' | 'chats' | 'channels' | 'contacts' = 'all';
  @Input() variant: 'sidebar' | 'dock' = 'sidebar';
  @Input() showHeader = true;
  @Input() externalSearch = '';
  @Input() channelsSubTabPreference: ChannelsSubTab | null = null;
  @Output() channelSelected = new EventEmitter<Channel>();

  @ViewChild('contactsSentinel')
  set contactsSentinel(el: ElementRef<HTMLElement> | undefined) {
    if (el) {
      this.observeContactsSentinel(el.nativeElement);
    } else {
      this.disconnectContactsObserver();
    }
  }

  @ViewChild('directSentinel')
  set directSentinel(el: ElementRef<HTMLElement> | undefined) {
    this.bindChannelSentinel('direct', el);
  }

  @ViewChild('groupSentinel')
  set groupSentinel(el: ElementRef<HTMLElement> | undefined) {
    this.bindChannelSentinel('group', el);
  }

  @ViewChild('entitySentinel')
  set entitySentinel(el: ElementRef<HTMLElement> | undefined) {
    this.bindChannelSentinel('entity_linked', el);
  }

  @ViewChild('dmSentinel')
  set dmSentinel(el: ElementRef<HTMLElement> | undefined) {
    if (el) {
      this.observeDmSentinel(el.nativeElement);
    } else {
      this.disconnectDmObserver();
    }
  }

  directChannels: Channel[] = [];
  groupChannels: Channel[] = [];
  entityChannels: Channel[] = [];
  private channelMap = new Map<number, Channel>();
  contactUsers: ContactUser[] = [];

  channelsSubTab: ChannelsSubTab = 'group';

  searchQuery = '';
  loading = false;
  contactsLoading = false;
  contactsLoadingMore = false;
  contactsHasMore = false;

  directHasMore = false;
  groupHasMore = false;
  entityHasMore = false;
  directLoadingMore = false;
  groupLoadingMore = false;
  entityLoadingMore = false;

  private readonly contactsPageSize = 50;
  private readonly channelsPageSize = 20;
  private contactsPage = 1;
  private contactsSearchTerm = '';
  private channelsSearchTerm = '';
  private contactsObserver: IntersectionObserver | null = null;
  private contactsFetchSub: Subscription | null = null;
  private channelSearch$ = new Subject<string>();

  private channelPages: Record<ChannelType, number> = {
    direct: 1,
    group: 1,
    entity_linked: 1,
  };
  private channelObservers = new Map<ChannelType, IntersectionObserver>();
  private channelsFetchSubs = new Map<ChannelType, Subscription>();
  private initialChannelLoadsInFlight = 0;

  // ─── New Channel dialog ────────────────────────────────────────────────────
  showNewChannelDialog = false;
  newChannelName = '';
  creating = false;
  creatingDmUserId: number | null = null;

  // ─── New DM dialog ─────────────────────────────────────────────────────────
  showNewDmDialog = false;
  dmSearchQuery = '';
  dmSearchResults: ContactUser[] = [];
  dmSearching = false;
  dmLoadingMore = false;
  dmHasMore = false;
  private readonly dmPageSize = 50;
  private dmPage = 1;
  private dmSearchTerm = '';
  private dmFetchSub: Subscription | null = null;
  private dmObserver: IntersectionObserver | null = null;
  private dmSearch$ = new Subject<string>();
  private contactsInitialized = false;
  private contactSearch$ = new Subject<string>();

  private destroy$ = new Subject<void>();

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.viewTab === 'channels' && this.channelsSubTabPreference) {
      this.channelsSubTab = this.channelsSubTabPreference;
    }
    this.loadChannelsForCurrentView();

    // Listen for WS events to refresh unread counts
    this.chatService.wsEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: WsEvent) => {
        switch (event.type) {
          case 'new_message':
            this.updateChannelOnNewMessage(
              event.channel_id,
              event.message.message_text,
              event.message.created_at
            );
            break;

          case 'mark_read_ack':
            this.clearChannelUnread(event.channel_id);
            break;

          case 'channel_added':
            this.handleChannelAdded(event);
            break;

          case 'member_added':
            this.handleMemberAdded(event);
            break;
        }
      });

    this.contactSearch$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        if (this.viewTab === 'contacts') {
          this.loadContacts(query);
        }
      });

    this.channelSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        if (this.viewTab === 'contacts') {
          return;
        }
        this.channelsSearchTerm = query;
        this.loadChannelsForCurrentView();
      });

    // Debounced DM user search
    this.dmSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.loadDmUsers(query?.trim() || '');
      });
    this.chatService.channelActivity$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        this.updateChannelPreview(
          update.channelId,
          update.lastMessage,
          update.lastMessageAt
        );
      });
  }

  private updateChannelPreview(
    channelId: number,
    lastMessage: string,
    lastMessageAt: string
  ): void {
    const channel = this.channelMap.get(channelId);

    if (!channel) {
      return;
    }

    channel.last_message = lastMessage;
    channel.last_message_at = lastMessageAt;

    channel.unread_count = 0;
    this.chatService.clearUnread(channelId);

    this.moveChannelToTop(channel);
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channelsSubTabPreference'] && this.channelsSubTabPreference) {
      this.applyChannelsSubTabPreference(this.channelsSubTabPreference);
    }

    if (changes['viewTab'] && !changes['viewTab'].firstChange) {
      if (this.viewTab === 'channels') {
        this.channelsSubTab = this.channelsSubTabPreference ?? 'group';
      }
      if (this.viewTab === 'contacts') {
        this.cancelAllChannelFetches();
        if (!this.contactsInitialized) {
          this.contactsInitialized = true;
          this.loadContacts(this.externalSearch?.trim() || '');
        }
      } else {
        this.loadChannelsForCurrentView();
      }
    } else if (changes['viewTab'] && this.viewTab === 'contacts') {
      if (!this.contactsInitialized) {
        this.contactsInitialized = true;
        this.loadContacts(this.externalSearch?.trim() || '');
      }
    }

    if (changes['externalSearch'] && !changes['externalSearch'].firstChange) {
      const query = this.externalSearch?.trim() || '';
      if (this.viewTab === 'contacts') {
        this.contactSearch$.next(query);
      } else {
        this.channelSearch$.next(query);
      }
    }
  }

  private applyChannelsSubTabPreference(tab: ChannelsSubTab): void {
    if (this.channelsSubTab === tab) {
      return;
    }
    this.channelsSubTab = tab;
    if (this.viewTab === 'channels') {
      this.loadChannels(tab);
    }
    this.cdr.markForCheck();
  }

  onSearchInput(): void {
    this.channelSearch$.next(this.searchQuery?.trim() || '');
  }

  ngOnDestroy(): void {
    this.contactsFetchSub?.unsubscribe();
    this.dmFetchSub?.unsubscribe();
    this.cancelAllChannelFetches();
    this.disconnectContactsObserver();
    this.disconnectDmObserver();
    this.disconnectAllChannelObservers();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setChannelsSubTab(tab: ChannelsSubTab): void {
    if (this.channelsSubTab === tab) {
      return;
    }
    this.channelsSubTab = tab;
    this.loadChannels(tab);
  }

  // ─── New group channel ─────────────────────────────────────────────────────

  private updateChannelOnNewMessage(
    channelId: number,
    lastMessage: string,
    lastMessageAt: string
  ): void {

    const channel = this.channelMap.get(channelId);

    if (!channel) {
      return;
    }

    channel.last_message = lastMessage;
    channel.last_message_at = lastMessageAt;

    channel.unread_count =
      channelId === this.activeChannelId
        ? 0
        : this.chatService.getUnreadCount(channelId);

    this.moveChannelToTop(channel);
    this.cdr.markForCheck();
  }

  private moveChannelToTop(channel: Channel): void {
    let list: Channel[];

    switch (channel.channel_type) {
      case 'direct':
        list = this.directChannels;
        break;

      case 'group':
        list = this.groupChannels;
        break;

      case 'entity_linked':
        list = this.entityChannels;
        break;

      default:
        return;
    }

    const index = list.indexOf(channel);

    if (index > 0) {
      list.splice(index, 1);
      list.unshift(channel);
    }
  }

  private clearChannelUnread(channelId: number): void {
    const channel = this.channelMap.get(channelId);

    if (!channel) {
      this.chatService.clearUnread(channelId);
      return;
    }

    channel.unread_count = 0;
    this.chatService.clearUnread(channelId);
    this.cdr.markForCheck();
  }

  private handleChannelAdded(event: any): void {
    const exists =
      this.directChannels.some(c => c.id === event.channel_id) ||
      this.groupChannels.some(c => c.id === event.channel_id) ||
      this.entityChannels.some(c => c.id === event.channel_id);

    if (exists) {
      return;
    }

    // Fetch the complete channel details
    this.chatService.getChannelDetail(event.channel_id).subscribe({
      next: (detail) => {
        const channel = detail.channel;
        this.channelMap.set(channel.id, channel);
        switch (channel.channel_type) {
          case 'direct':
            this.directChannels.unshift(channel);
            break;

          case 'group':
            this.groupChannels.unshift(channel);
            break;

          case 'entity_linked':
            this.entityChannels.unshift(channel);
            break;
        }

        // Subscribe to the new channel
        this.chatService.subscribeToChannel(channel.id);

        channel.unread_count =
          this.chatService.getUnreadCount(channel.id) ||
          channel.unread_count ||
          0;

        this.cdr.markForCheck();
      }
    });
  }

  private handleMemberAdded(event: any): void {
    const channel = this.channelMap.get(event.channel_id);

    if (!channel) {
      return;
    }

    channel.member_count = (channel.member_count || 0) + event.user_ids.length;

    this.cdr.markForCheck();
  }

  openNewChannelDialog(): void {
    this.newChannelName = '';
    this.showNewChannelDialog = true;
  }

  createNewChannel(): void {
    const name = this.newChannelName.trim();
    if (!name || this.creating) return;
    this.creating = true;
    this.cdr.markForCheck();
    this.chatService
      .createChannel({ channel_type: 'group', channel_name: name, member_ids: [] })
      .subscribe({
        next: (res) => {
          this.creating = false;
          this.showNewChannelDialog = false;
          // this.loadChannels();
          // Auto-open the new channel
          // this.chatService.getChannelDetail(res.channel_id).subscribe({
          //   next: (detail) => this.channelSelected.emit(detail.channel),
          // });
          this.creating = false;
          this.showNewChannelDialog = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.creating = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ─── New DM ────────────────────────────────────────────────────────────────

  openNewDmDialog(): void {
    this.dmFetchSub?.unsubscribe();
    this.dmSearchQuery = '';
    this.dmSearchResults = [];
    this.dmSearchTerm = '';
    this.dmPage = 1;
    this.dmHasMore = false;
    this.dmSearching = false;
    this.dmLoadingMore = false;
    if (!this.creating) {
      this.creatingDmUserId = null;
    }
    this.showNewDmDialog = true;
    this.cdr.markForCheck();
  }

  onDmSearchInput(): void {
    this.dmSearch$.next(this.dmSearchQuery);
  }

  loadDmUsers(searchTerm: string, append = false): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.dmFetchSub?.unsubscribe();
      this.dmSearchTerm = '';
      this.dmSearchResults = [];
      this.dmHasMore = false;
      this.dmSearching = false;
      this.dmLoadingMore = false;
      this.cdr.markForCheck();
      return;
    }

    if (append) {
      if (!this.dmHasMore || this.dmSearching || this.dmLoadingMore) {
        return;
      }
      this.dmLoadingMore = true;
    } else {
      this.dmFetchSub?.unsubscribe();
      this.dmSearchTerm = searchTerm;
      this.dmPage = 1;
      this.dmHasMore = false;
      this.dmSearching = true;
      this.dmLoadingMore = false;
    }

    this.cdr.markForCheck();

    const pageToLoad = append ? this.dmPage + 1 : 1;

    this.dmFetchSub = this.chatService
      .searchUsers(this.dmSearchTerm, pageToLoad, this.dmPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.dmSearchResults = append
            ? this.mergeUsers(this.dmSearchResults, results.users)
            : results.users;
          this.dmPage = pageToLoad;
          this.dmHasMore = !results.last;
          this.dmSearching = false;
          this.dmLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (!append) {
            this.dmSearchResults = [];
            this.dmHasMore = false;
          }
          this.dmSearching = false;
          this.dmLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private mergeUsers(
    existing: ContactUser[],
    incoming: ContactUser[]
  ): ContactUser[] {
    const seen = new Set(existing.map((u) => u.user_id));
    return [...existing, ...incoming.filter((u) => !seen.has(u.user_id))];
  }

  private observeDmSentinel(element: HTMLElement): void {
    this.disconnectDmObserver();

    const root = element.closest('.dm-results');
    this.dmObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadDmUsers(this.dmSearchTerm, true);
        }
      },
      {
        root: root instanceof Element ? root : null,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    this.dmObserver.observe(element);
  }

  private disconnectDmObserver(): void {
    this.dmObserver?.disconnect();
    this.dmObserver = null;
  }

  startDm(user: { user_id: number; user_name: string; email: string }): void {
    if (this.creating) return;

    const existing = this.findExistingDirectChannel(user.user_id, user.user_name);
    if (existing) {
      this.showNewDmDialog = false;
      this.selectChannel(existing);
      return;
    }

    this.creating = true;
    this.creatingDmUserId = user.user_id;
    this.cdr.markForCheck();
    this.chatService
      .createChannel({
        channel_type: 'direct',
        channel_name: user.user_name,
        member_ids: [user.user_id],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.showNewDmDialog = false;
          this.chatService
            .getChannelDetail(res.channel_id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (detail) => {
                this.upsertDirectChannel(detail.channel);
                this.selectChannel(detail.channel);
                this.creating = false;
                this.creatingDmUserId = null;
                this.cdr.markForCheck();
              },
              error: () => {
                this.creating = false;
                this.creatingDmUserId = null;
                this.cdr.markForCheck();
              },
            });
        },
        error: () => {
          this.creating = false;
          this.creatingDmUserId = null;
          this.cdr.markForCheck();
        },
      });
  }

  private findExistingDirectChannel(userId?: number, userName?: string): Channel | undefined {
    const normalized = userName?.trim().toLowerCase();
    return this.directChannels.find(
      (ch) => normalized && ch.channel_name?.trim().toLowerCase() === normalized
    );
  }

  private upsertDirectChannel(channel: Channel): void {
    this.channelMap.set(channel.id, channel);
    const normName = channel.channel_name?.trim().toLowerCase();
    const existingIndex = this.directChannels.findIndex(
      (c) => c.id === channel.id || (normName && c.channel_name?.trim().toLowerCase() === normName)
    );
    if (existingIndex !== -1) {
      this.directChannels[existingIndex] = channel;
    } else {
      this.directChannels.unshift(channel);
    }
    this.chatService.subscribeToChannel(channel.id);
  }

  loadChannelsForCurrentView(): void {
    this.cancelAllChannelFetches();
    this.syncChannelsSearchTermFromInputs();

    switch (this.viewTab) {
      case 'chats':
        this.loadChannels('direct');
        break;
      case 'channels':
        this.loadChannels(this.channelsSubTab);
        break;
      case 'contacts':
        break;
      case 'all':
      default:
        this.loadChannels('direct');
        this.loadChannels('group');
        this.loadChannels('entity_linked');
        break;
    }
  }

  private syncChannelsSearchTermFromInputs(): void {
    const query =
      (this.variant === 'dock'
        ? this.externalSearch
        : this.searchQuery
      )?.trim() || '';
    this.channelsSearchTerm = query;
  }

  loadChannels(channelType: ChannelType, append = false): void {
    if (append) {
      if (
        !this.getHasMore(channelType) ||
        this.loading ||
        this.getLoadingMore(channelType)
      ) {
        return;
      }
      this.setLoadingMore(channelType, true);
    } else {
      const hadPendingInitial = this.channelsFetchSubs.has(channelType);
      this.cancelChannelFetch(channelType);
      if (hadPendingInitial) {
        this.initialChannelLoadsInFlight = Math.max(
          0,
          this.initialChannelLoadsInFlight - 1
        );
      }
      this.channelPages[channelType] = 1;
      this.setHasMore(channelType, false);
      this.setLoadingMore(channelType, false);
      this.initialChannelLoadsInFlight += 1;
      this.loading = true;
    }

    this.cdr.markForCheck();

    const pageToLoad = append ? this.channelPages[channelType] + 1 : 1;
    const search = this.channelsSearchTerm || undefined;

    const sub = this.chatService
      .getMyChannels(pageToLoad, this.channelsPageSize, channelType, search)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          res.channels.forEach((ch) => {
            const live = this.chatService.getUnreadCount(ch.id);
            if (live > (ch.unread_count || 0)) {
              ch.unread_count = live;
            }
          });
          this.chatService.updateUnreadCounts(res.channels);
          res.channels.forEach((ch) => this.chatService.subscribeToChannel(ch.id));
          this.applyChannels(res.channels, channelType, append);
          this.channelPages[channelType] = pageToLoad;
          this.setHasMore(channelType, !!res.has_more);
          this.finishChannelLoad(channelType, append);
        },
        error: () => {
          if (!append) {
            this.applyChannels([], channelType, false);
            this.setHasMore(channelType, false);
          }
          this.finishChannelLoad(channelType, append);
        },
      });

    this.channelsFetchSubs.set(channelType, sub);
  }

  private finishChannelLoad(channelType: ChannelType, append: boolean): void {
    if (append) {
      this.setLoadingMore(channelType, false);
    } else {
      this.initialChannelLoadsInFlight = Math.max(
        0,
        this.initialChannelLoadsInFlight - 1
      );
      this.loading = this.initialChannelLoadsInFlight > 0;
    }
    this.cdr.markForCheck();
  }

  private applyChannels(
    channels: Channel[],
    channelType: ChannelType,
    append: boolean
  ): void {
    switch (channelType) {
      case 'direct':
        this.directChannels = append
          ? this.mergeChannels(this.directChannels, channels)
          : channels;
        break;
      case 'group':
        this.groupChannels = append
          ? this.mergeChannels(this.groupChannels, channels)
          : channels;
        break;
      case 'entity_linked':
        this.entityChannels = append
          ? this.mergeChannels(this.entityChannels, channels)
          : channels;
        break;
    }

    this.rebuildChannelMap();
  }

  private mergeChannels(existing: Channel[], incoming: Channel[]): Channel[] {
    const seenIds = new Set(existing.map((c) => c.id));
    const merged = [...existing];
    for (const inc of incoming) {
      if (seenIds.has(inc.id)) {
        continue;
      }
      if (inc.channel_type === 'direct' && inc.channel_name) {
        const normInc = inc.channel_name.trim().toLowerCase();
        const existsByName = merged.some(
          (c) => c.channel_type === 'direct' && c.channel_name?.trim().toLowerCase() === normInc
        );
        if (existsByName) {
          continue;
        }
      }
      if (inc.channel_type === 'entity_linked' && inc.linked_entity_type && inc.linked_entity_id) {
        const existsByEntity = merged.some(
          (c) =>
            c.channel_type === 'entity_linked' &&
            c.linked_entity_type === inc.linked_entity_type &&
            String(c.linked_entity_id) === String(inc.linked_entity_id)
        );
        if (existsByEntity) {
          continue;
        }
      }
      seenIds.add(inc.id);
      merged.push(inc);
    }
    return merged;
  }

  private rebuildChannelMap(): void {
    this.channelMap.clear();
    [
      ...this.directChannels,
      ...this.groupChannels,
      ...this.entityChannels,
    ].forEach((channel) => {
      this.channelMap.set(channel.id, channel);
    });
  }

  private getHasMore(channelType: ChannelType): boolean {
    switch (channelType) {
      case 'direct':
        return this.directHasMore;
      case 'group':
        return this.groupHasMore;
      case 'entity_linked':
        return this.entityHasMore;
    }
  }

  private setHasMore(channelType: ChannelType, value: boolean): void {
    switch (channelType) {
      case 'direct':
        this.directHasMore = value;
        break;
      case 'group':
        this.groupHasMore = value;
        break;
      case 'entity_linked':
        this.entityHasMore = value;
        break;
    }
  }

  private getLoadingMore(channelType: ChannelType): boolean {
    switch (channelType) {
      case 'direct':
        return this.directLoadingMore;
      case 'group':
        return this.groupLoadingMore;
      case 'entity_linked':
        return this.entityLoadingMore;
    }
  }

  private setLoadingMore(channelType: ChannelType, value: boolean): void {
    switch (channelType) {
      case 'direct':
        this.directLoadingMore = value;
        break;
      case 'group':
        this.groupLoadingMore = value;
        break;
      case 'entity_linked':
        this.entityLoadingMore = value;
        break;
    }
  }

  private bindChannelSentinel(
    channelType: ChannelType,
    el: ElementRef<HTMLElement> | undefined
  ): void {
    if (el) {
      this.observeChannelSentinel(channelType, el.nativeElement);
    } else {
      this.disconnectChannelObserver(channelType);
    }
  }

  private observeChannelSentinel(
    channelType: ChannelType,
    element: HTMLElement
  ): void {
    this.disconnectChannelObserver(channelType);

    const root = element.closest('.cl-scroll');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadChannels(channelType, true);
        }
      },
      {
        root: root instanceof Element ? root : null,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    observer.observe(element);
    this.channelObservers.set(channelType, observer);
  }

  private disconnectChannelObserver(channelType: ChannelType): void {
    this.channelObservers.get(channelType)?.disconnect();
    this.channelObservers.delete(channelType);
  }

  private disconnectAllChannelObservers(): void {
    this.channelObservers.forEach((observer) => observer.disconnect());
    this.channelObservers.clear();
  }

  private cancelChannelFetch(channelType: ChannelType): void {
    this.channelsFetchSubs.get(channelType)?.unsubscribe();
    this.channelsFetchSubs.delete(channelType);
  }

  private cancelAllChannelFetches(): void {
    this.channelsFetchSubs.forEach((sub) => sub.unsubscribe());
    this.channelsFetchSubs.clear();
    this.initialChannelLoadsInFlight = 0;
    this.loading = false;
  }

  selectChannel(channel: Channel): void {
    this.activeChannelId = channel.id;
    channel.unread_count = 0;
    this.chatService.clearUnread(channel.id);
    this.channelSelected.emit(channel);
    this.cdr.markForCheck();
  }

  getChannelDisplayName(channel: Channel): string {
    if (channel.channel_name) return channel.channel_name;
    if (channel.channel_type === 'direct') return `DM #${channel.id}`;
    if (channel.channel_type === 'entity_linked') {
      return `${channel.linked_entity_type} #${channel.linked_entity_id}`;
    }
    return `Channel #${channel.id}`;
  }

  getChannelIcon(channel: Channel): string {
    if (channel.channel_type === 'direct') return 'pi pi-user';
    if (channel.channel_type === 'entity_linked') return 'pi pi-tag';
    return 'pi pi-comments';
  }

  get filteredDirect(): Channel[] {
    return this.directChannels;
  }

  get filteredGroup(): Channel[] {
    return this.groupChannels;
  }

  get filteredEntity(): Channel[] {
    return this.entityChannels;
  }

  get filteredContacts() {
    return this.contactUsers;
  }

  get showChatsSection(): boolean {
    return this.viewTab === 'all' || this.viewTab === 'chats';
  }

  get showChannelsSection(): boolean {
    return this.viewTab === 'all' || this.viewTab === 'channels';
  }

  get showGroupChannels(): boolean {
    return this.viewTab === 'all' || this.channelsSubTab === 'group';
  }

  get showEntityChannels(): boolean {
    return this.viewTab === 'all' || this.channelsSubTab === 'entity_linked';
  }

  get showContactsSection(): boolean {
    return this.viewTab === 'contacts';
  }

  loadContacts(searchTerm: string = '', append: boolean = false): void {
    if (append) {
      if (!this.contactsHasMore || this.contactsLoading || this.contactsLoadingMore) {
        return;
      }
      this.contactsLoadingMore = true;
    } else {
      this.contactsFetchSub?.unsubscribe();
      this.contactsSearchTerm = searchTerm;
      this.contactsPage = 1;
      this.contactsHasMore = false;
      this.contactsLoading = true;
      this.contactsLoadingMore = false;
    }

    this.cdr.markForCheck();

    const pageToLoad = append ? this.contactsPage + 1 : 1;

    this.contactsFetchSub = this.chatService
      .searchUsers(this.contactsSearchTerm, pageToLoad, this.contactsPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.contactUsers = append
            ? [...this.contactUsers, ...results.users]
            : results.users;
          this.contactsPage = pageToLoad;
          this.contactsHasMore = !results.last;
          this.contactsLoading = false;
          this.contactsLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (!append) {
            this.contactUsers = [];
            this.contactsHasMore = false;
          }
          this.contactsLoading = false;
          this.contactsLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private observeContactsSentinel(element: HTMLElement): void {
    this.disconnectContactsObserver();

    const root = element.closest('.cl-scroll');
    this.contactsObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadContacts(this.contactsSearchTerm, true);
        }
      },
      {
        root: root instanceof Element ? root : null,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    this.contactsObserver.observe(element);
  }

  private disconnectContactsObserver(): void {
    this.contactsObserver?.disconnect();
    this.contactsObserver = null;
  }

  onContactClick(user: { user_id: number; user_name: string; email: string }): void {
    this.startDm(user);
  }
}
